import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Camera, Trash2, RefreshCw, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const fileUrl = (v) => {
  if (!v) return "";
  if (v.startsWith("http") || v.startsWith("data:")) return v;
  return `${process.env.REACT_APP_BACKEND_URL}${v}`;
};

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
}

export function ImageUpload({ value, onChange, label, testid = "image" }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file.");
    setBusy(true);
    try { onChange(await uploadFile(file)); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      {label && <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</label>}
      <input ref={inputRef} data-testid={`${testid}-input`} type="file" accept="image/*" className="hidden" onChange={onFile} />
      {value ? (
        <div className="mt-1">
          <div className="relative overflow-hidden rounded-lg border border-border">
            <img src={fileUrl(value)} alt="preview" className="h-40 w-full object-cover" />
          </div>
          <div className="mt-2 flex gap-2">
            <button type="button" data-testid={`${testid}-replace`} onClick={() => inputRef.current?.click()} disabled={busy}
              className="flex h-10 flex-1 items-center justify-center gap-1.5 rounded-full border border-border text-sm font-semibold text-primary disabled:opacity-50">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Replace
            </button>
            <button type="button" data-testid={`${testid}-delete`} onClick={() => onChange("")}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-border text-destructive"><Trash2 size={16} /></button>
          </div>
        </div>
      ) : (
        <button type="button" data-testid={`${testid}-add`} onClick={() => inputRef.current?.click()} disabled={busy}
          className="mt-1 flex h-40 w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border bg-muted/40 text-sm font-semibold text-muted-foreground transition-colors hover:border-secondary disabled:opacity-60">
          {busy ? <><Loader2 size={22} className="animate-spin" /> Uploading…</> : <><Camera size={22} /> Choose or Take Photo</>}
        </button>
      )}
    </div>
  );
}

export function ImageUploadMulti({ value = [], onChange, label, testid = "images" }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const photos = Array.isArray(value) ? value : [];

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    try {
      const urls = [];
      for (const f of files) {
        if (!f.type.startsWith("image/")) continue;
        urls.push(await uploadFile(f));
      }
      onChange([...photos, ...urls]);
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  const remove = (i) => onChange(photos.filter((_, idx) => idx !== i));

  return (
    <div>
      {label && <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</label>}
      <input ref={inputRef} data-testid={`${testid}-input`} type="file" accept="image/*" multiple className="hidden" onChange={onFile} />
      <div className="mt-1 grid grid-cols-3 gap-2">
        {photos.map((p, i) => (
          <div key={p} className="relative overflow-hidden rounded-lg border border-border">
            <img src={fileUrl(p)} alt="" className="h-24 w-full object-cover" />
            <button type="button" data-testid={`${testid}-remove-${i}`} onClick={() => remove(i)}
              className="absolute right-1 top-1 grid h-7 w-7 place-items-center rounded-full bg-white/90 text-destructive"><Trash2 size={13} /></button>
          </div>
        ))}
        <button type="button" data-testid={`${testid}-add`} onClick={() => inputRef.current?.click()} disabled={busy}
          className="flex h-24 flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-border bg-muted/40 text-xs font-semibold text-muted-foreground hover:border-secondary disabled:opacity-60">
          {busy ? <Loader2 size={18} className="animate-spin" /> : <><Plus size={18} /> Add</>}
        </button>
      </div>
    </div>
  );
}
